/** One robot: what it is doing, what it is set to, and the way into the headset. */
import { Stream, type StreamStats, useRobotOnline, useRobotState, useTrackMeta, useTracks } from "adamo-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useActions } from "../adamo/actions";
import { useBoxMap } from "../adamo/boxmap";
import { useConnection } from "../adamo/connection";
import { useSettings } from "../adamo/settings";
import { useStatus } from "../adamo/status";
import { SCENE_TRACK, WRIST_TRACK } from "../adamo/topics";
import type { Driver, PathState } from "../adamo/types";
import { Banner } from "../components/Banner";
import { BoxMapPanel } from "../components/BoxMapPanel";
import { DriverBadge } from "../components/DriverBadge";
import { LinkPanel } from "../components/LinkPanel";
import { SettingsForm } from "../components/SettingsForm";
import { StatusPanel } from "../components/StatusPanel";
import { operatorId } from "../state/operatorId";
import { VrButton } from "../vr/VrButton";

const STALE_AFTER_S = 3;

export function RobotPage() {
  const { id = "" } = useParams();
  const { org, me, reportTrouble } = useConnection();
  const online = useRobotOnline(id);
  const tracks = useTracks(id);
  const trackMeta = useTrackMeta(id);
  const { status, feed } = useStatus(org, id);
  const boxMap = useBoxMap(org, id);
  const [lost, setLost] = useState<string | null>(null);
  // The arm tells us the name it listens on. The configured one is only the opening guess,
  // for the moment before the first status arrives.
  const operator = status?.operator ?? me?.operator ?? "quest-operator";
  const sender = useActions(org, operator, feed, (name) => setLost(name));
  const [ignoredSetting, setIgnoredSetting] = useState<string | null>(null);
  const { settings, change, understood, applyPreferences, savedDiffer } =
    useSettings(id, status, sender, (key) => setIgnoredSetting(key));
  const [offerDismissed, setOfferDismissed] = useState(false);
  const path: PathState = useRobotState<PathState>(id, "path") ?? status?.taught ?? { start: false, waypoints: 0, end: false };
  const driver: Driver = useRobotState<Driver>(id, "driver") ?? status?.driver ?? null;
  const [, tick] = useState(0);
  // This browser's own view of the preview, which is not the operator's: a laptop across the room
  // sees the video by a different route than the headset does. Shown small, beside the picture.
  const [preview, setPreview] = useState<StreamStats | null>(null);

  useEffect(() => {
    const timer = window.setInterval(() => tick((n) => n + 1), 1000);
    return () => window.clearInterval(timer);
  }, []);

  // A publisher that cannot reach the relay is the clearest sign the socket has gone; the
  // supervisor decides whether that is worth rebuilding the session for.
  useEffect(() => {
    const onDown = (e: Event) => reportTrouble(`the relay connection dropped (${(e as CustomEvent).detail})`);
    window.addEventListener("adamo-link-down", onDown);
    return () => window.removeEventListener("adamo-link-down", onDown);
  }, [reportTrouble]);

  const live = useMemo(() => new Set(tracks.filter((t) => t.isActive).map((t) => t.name)), [tracks]);
  // Which track the headset shows, and whether to split it between the eyes. Decided by the
  // track's own metadata rather than by its name: the arm has called its stereo track `main`
  // for as long as it has had one, and a portal that only recognised one spelling would show
  // a stereo pair squashed flat and give no hint why.
  const vrTrack = useMemo(() => {
    const active = tracks.filter((t) => t.isActive);
    const stereo = active.find((t) => trackMeta.get(t.name)?.stereo);
    if (stereo) return { name: stereo.name, stereo: true };
    const wrist = active.find((t) => t.name === WRIST_TRACK) ?? active[0];
    return wrist ? { name: wrist.name, stereo: false } : null;
  }, [tracks, trackMeta]);
  const stale = feed.age() > STALE_AFTER_S;
  const mine = driver?.lease_ok === true && driver.operator_id === operatorId();
  const busyElsewhere = driver?.lease_ok === true && !mine;

  const claim = useCallback(() => {
    sender.own(driver?.lease_ok ? "take_over" : "claim", { display_name: me?.email ?? "the portal" });
  }, [sender, driver, me]);
  const release = useCallback(() => sender.own("release_control"), [sender]);

  if (online === false) {
    return (
      <>
        <h1>{id}</h1>
        <Banner tone="warn">This robot is not on the relay right now. It will come back here when it is.</Banner>
        <p className="note"><Link to="/">Back to the live sessions</Link></p>
      </>
    );
  }

  return (
    <>
      <h1>{id}</h1>
      {lost && <Banner tone="bad">“{lost}” was never acknowledged by the robot. Try it again.</Banner>}
      {status && !understood && (
        <Banner tone="warn">
          This arm is running an older program that has no settings, no taught-path buttons and no
          notion of who is driving. Changes here are sent and ignored, which is why a slider moves
          and then goes back. Everything else on this page works.
        </Banner>
      )}
      {understood && savedDiffer.length > 0 && !offerDismissed && !busyElsewhere && (
        <Banner tone="quiet">
          This browser remembers {savedDiffer.map(([k]) => String(k).replace(/_/g, " ")).join(", ")} set
          differently from this arm.{" "}
          <button onClick={() => { applyPreferences(); setOfferDismissed(true); }}>Use mine</button>{" "}
          <button onClick={() => setOfferDismissed(true)}>Keep the arm's</button>
        </Banner>
      )}
      {ignoredSetting && understood && (
        <Banner tone="warn">
          The arm did not take “{ignoredSetting}”. It may be busy, or refusing the value.
        </Banner>
      )}
      {status?.rim && status.rim.level !== "clear" && (
        <Banner tone={status.rim.level === "hit" ? "bad" : "warn"}>
          Camera mount {status.rim.level === "hit" ? "will hit" : "is close to"} the box: {status.rim.say}
          {status.rim.clear_mm !== null && ` (${Math.max(status.rim.clear_mm, 0)} mm)`}
        </Banner>
      )}
      {busyElsewhere && (
        <Banner tone="warn">
          {driver?.display_name || "Someone else"} is driving. Your controls will not reach the arm until you take over.
        </Banner>
      )}
      <div className="session">
        <div>
          <div className="video">
            {/* Whatever the headset would show. Turning stereo on replaces the mono wrist
                track rather than adding to it, so naming one here would blank the preview
                at exactly the moment someone switched. A side-by-side pair previews as
                both halves, which is the honest thing to show on a flat screen. */}
            {vrTrack
              ? <Stream robot={id} track={vrTrack.name} onStats={setPreview} />
              : <p className="empty">no camera streaming from this robot</p>}
            <span className="label">
              {vrTrack ? `${vrTrack.name}${vrTrack.stereo ? " · stereo pair" : ""}` : "no video"}
              {vrTrack && preview && ` · here ${Math.round(preview.fps)} fps${preview.glassToGlassMs
                ? `, ${Math.round(preview.glassToGlassMs)} ms behind` : ""}`}
            </span>
          </div>
          {live.has(SCENE_TRACK) && (
            <div className="video" style={{ marginTop: 12, aspectRatio: "16 / 9" }}>
              <Stream robot={id} track={SCENE_TRACK} />
              <span className="label">{SCENE_TRACK}</span>
            </div>
          )}
          <div className="panel" style={{ marginTop: 12 }}>
            <h2>The box, as the wrist camera has mapped it</h2>
            <BoxMapPanel map={boxMap.map} tool={boxMap.tool} feed={feed} />
          </div>
          <div className="panel" style={{ marginTop: 12 }}>
            <h2>What it is doing</h2>
            <StatusPanel status={status} stale={stale} />
          </div>
          <div className="panel" style={{ marginTop: 12 }}>
            <h2>The link</h2>
            <LinkPanel status={status} stale={stale} />
          </div>
        </div>

        <div>
          <div className="panel">
            <VrButton robot={id} track={vrTrack} org={org} operator={operator}
                      feed={feed} sender={sender} settings={settings} change={change}
                      path={path} driver={driver} onClaim={claim} boxMap={boxMap.latest} toolModel={boxMap.toolLatest}
                      settingsUnderstood={understood} />
            <div className="rows" style={{ marginTop: 12 }}>
              <DriverBadge driver={driver} onClaim={claim} onRelease={release} />
            </div>
          </div>

          <div className="panel" style={{ marginTop: 12 }}>
            <SettingsForm settings={settings} path={path} change={change} sender={sender}
                          disabled={busyElsewhere} state={status?.state ?? null} />
          </div>

          <div className="panel" style={{ marginTop: 12 }}>
            <h2>The cycle</h2>
            {/* Not greyed out by state. The arm is the authority on what it will accept right
                now and says so in its own words, and a panel of dead buttons reads as a broken
                page rather than as an arm that is simply idle. */}
            <div className="buttons">
              <button disabled={busyElsewhere} onClick={() => sender.send("enable")}>Enable</button>
              <button disabled={busyElsewhere} onClick={() => sender.send("picked")}>Picked</button>
              <button disabled={busyElsewhere} onClick={() => sender.send("reset")}>Start this pick again</button>
              <button disabled={busyElsewhere}
                      onClick={() => sender.send("pause")}>{status?.paused ? "Resume" : "Pause"}</button>
              <button className="warn" disabled={busyElsewhere}
                      onClick={() => sender.send("release")}>Release</button>
            </div>
            {status?.message && <p className="note" style={{ marginTop: 8 }}>{status.message}</p>}
          </div>
        </div>
      </div>
    </>
  );
}
